import * as LtsConstants from '../constants/LtsConstants';
import { addDays } from '../../../utils/commonUtils';
import fecha from 'fecha';
import { formatDateForLtsApiResponse, getDateObjectFromDetails } from './commonLtsUtils';
import findLast from 'lodash.findlast';
import { postRequest } from '../../../utils/clientApi';

function fillMissingDataForIntermediateStationFromHaltingStation({ haltingStation, intermediateStation }) {
  const { distanceFromOriginStation, stoppingStation, divertedStation, cancelledStation } = haltingStation;
  for (const [key, value] of Object.entries({
    distanceFromOriginStation,
    stoppingStation,
    divertedStation,
    cancelledStation
  })) {
    intermediateStation[key] = intermediateStation[key] || value;
  }
}

function getFormattedDateStringForLts(dateObject) {
  return fecha.format(dateObject, 'DD-MM-YYYY HH-mm-ss');
}

function collectFromIntermediateScheduleForTrainStartDate(trainStartDate) {
  return {
    collectHaltingStationDetailsFromIntermediateSchedule,
    collectIntermediateStationDetailsFromIntermediateSchedule
  };

  function collectHaltingStationDetailsFromIntermediateSchedule(stationDetailScheduledIntermediateApi) {
    const {
      Station: { name: stationName, code: stationCode },
      Distance: distanceFromOriginStation
    } = stationDetailScheduledIntermediateApi;
    const { scheduledDepartureDate, scheduledDepartureTime, scheduledArrivalDate, scheduledArrivalTime } =
      getArrivalAndDepartureDateFromIntermediateStation({
        trainStartDate,
        intermediateScheduleStationDetail: stationDetailScheduledIntermediateApi
      });
    const stoppingMinutes = getStoppingMinutes({
      scheduledDepartureTime,
      scheduledDepartureDate,
      scheduledArrivalTime,
      scheduledArrivalDate
    });

    return {
      stationName,
      stationCode,
      scheduledDepartureDetails: {
        scheduledDepartureTime,
        scheduledDepartureDate
      },
      scheduledArrivalDetails: {
        scheduledArrivalTime,
        scheduledArrivalDate
      },
      stoppingMinutes: stoppingMinutes.toString(),
      distanceFromOriginStation
    };
  }
  function collectIntermediateStationDetailsFromIntermediateSchedule(intermediateStationDetail) {
    const {
      Station: { name: stationName, code: stationCode },
      Distance: distanceFromOriginStation
    } = intermediateStationDetail;

    const { scheduledDepartureDate, scheduledDepartureTime, scheduledArrivalDate, scheduledArrivalTime } =
      getArrivalAndDepartureDateFromIntermediateStation({
        trainStartDate,
        intermediateScheduleStationDetail: intermediateStationDetail
      });

    return {
      stationName,
      stationCode,
      scheduledDepartureDetails: {
        scheduledDepartureTime,
        scheduledDepartureDate
      },
      scheduledArrivalDetails: {
        scheduledArrivalTime,
        scheduledArrivalDate
      },
      distanceFromOriginStation
    };
  }
}

export async function getLtsStationDetails({ trainNumber, trainStartDate }) {
  const {
    LIVE_STATUS_API_ENDPOINT,
    SCHEDULED_INTERMEDIATE_API_ENDPOINT,
    LTS_TRACKING_PARAMS,
    LTS_CUSTOM_HTTP_HEADERS
  } = LtsConstants;
  let liveStatusApiResponse;
  let scheduledIntermediateApiResponse;
  try {
    liveStatusApiResponse = await postRequest(
      LIVE_STATUS_API_ENDPOINT,
      {
        trainNumber,
        ...(trainStartDate ? { dateOfJourney: trainStartDate } : {}),
        trackingParams: LTS_TRACKING_PARAMS
      },
      LTS_CUSTOM_HTTP_HEADERS
    );
  } catch (err) {
    /* eslint-disable no-console */
    console.log('Error in fetching liveStatus Response LTS');
  }

  try {
    scheduledIntermediateApiResponse = await postRequest(
      SCHEDULED_INTERMEDIATE_API_ENDPOINT,
      {
        trainNumber,
        ...(trainStartDate ? { trainStartDate: trainStartDate } : {}),
        trackingParams: LTS_TRACKING_PARAMS
      },
      LTS_CUSTOM_HTTP_HEADERS
    );
  } catch (err) {
    console.log('Error in fetching scheduled intermediate Response LTS');
  }
  return decorateLtsData({
    liveStatusApiResponse,
    scheduledIntermediateApiResponse,
    trainStartDate
  });
}

export function decorateLtsData({ liveStatusApiResponse, scheduledIntermediateApiResponse, trainStartDate }) {
  trainStartDate = formatDateForLtsApiResponse(trainStartDate);
  const liveStationResponseProper = liveStatusApiResponse && !liveStatusApiResponse.Error;
  const scheduledIntermediateResponseProper =
    scheduledIntermediateApiResponse && !scheduledIntermediateApiResponse.Error;

  if (liveStationResponseProper && scheduledIntermediateResponseProper) {
    const result = getStationDetailsFromBothLiveStatusAndScheduledIntermediateApi({
      liveStatusApiResponse,
      scheduledIntermediateApiResponse
    });
    return result;
  }
  if (scheduledIntermediateResponseProper) {
    return getStationDetailsFromOnlyScheduledIntermediateApi({
      scheduledIntermediateApiResponse,
      trainStartDate
    });
  }
  return null;
}

function getStoppingMinutes({
  scheduledDepartureTime,
  scheduledDepartureDate,
  scheduledArrivalTime,
  scheduledArrivalDate
}) {
  const scheduledArrivalDateObject = getDateObjectFromDetails({
    dateInString: scheduledArrivalDate,
    timeInString: scheduledArrivalTime
  });
  const scheduledDepartureDateObject = getDateObjectFromDetails({
    dateInString: scheduledDepartureDate,
    timeInString: scheduledDepartureTime
  });
  const differenceInDates = scheduledDepartureDateObject - scheduledArrivalDateObject;
  const NUMBER_OF_MILLISECONDS_IN_A_MINUTE = 60000;
  const stoppingTimeInMinutes = Math.round(differenceInDates / NUMBER_OF_MILLISECONDS_IN_A_MINUTE);
  return stoppingTimeInMinutes;
}

function getStationStatusIntermediateScheduleResponse(haltingStationCollection) {
  const originStation = haltingStationCollection[0];
  const destinationStation = haltingStationCollection[haltingStationCollection.length - 1];
  return {
    currentStation: undefined,
    nextStation: undefined,
    currentIntermediateStation: undefined,
    originStation,
    destinationStation,
    trainStartedFromSource: undefined,
    trainReachedDestination: undefined,
    trainFullyDiverted: undefined,
    trainFullyCancelled: undefined,
    firstStoppingStation: undefined,
    lastStoppingStation: undefined
  };
}

function getStationStatusBothApiResponse({ liveStatusApiResponse, haltingStationCollection }) {
  const originStation = haltingStationCollection[0];
  const destinationStation = haltingStationCollection[haltingStationCollection.length - 1];
  const {
    MetaDetails: {
      CurrentIntermediateStation: { code: currentIntermediateStationCode } = {},
      CurrentStation: {
        Station: { code: currentStationCode }
      }
    },
    TrainDetails: { Departed: trainStartedFromSource, Terminated: trainReachedDestination }
  } = liveStatusApiResponse;
  const currentStationIndex = haltingStationCollection.findIndex(
    (haltingStation) => haltingStation.stationCode === currentStationCode
  );
  const nextStationIndex = currentStationIndex + 1;
  const currentStation = haltingStationCollection[currentStationIndex];
  const nextStation = haltingStationCollection[nextStationIndex];
  const currentIntermediateStation = currentStation.intermediateStations.find(
    (intermediateStation) => intermediateStation.stationCode === currentIntermediateStationCode
  );
  const trainFullyDiverted = haltingStationCollection.every((haltingStation) => haltingStation.divertedStation);
  const trainFullyCancelled = haltingStationCollection.every((haltingStation) => haltingStation.cancelledStation);
  const firstStoppingStation = haltingStationCollection.find(
    (haltingStation) =>
      !haltingStation.divertedStation && !haltingStation.cancelledStation && haltingStation.stoppingStation
  );
  const lastStoppingStation = findLast(
    haltingStationCollection,
    (haltingStation) =>
      !haltingStation.divertedStation && !haltingStation.cancelledStation && haltingStation.stoppingStation
  );
  return {
    originStation,
    destinationStation,
    currentIntermediateStation,
    currentStation,
    nextStation,
    trainStartedFromSource,
    trainReachedDestination,
    trainFullyDiverted,
    trainFullyCancelled,
    firstStoppingStation,
    lastStoppingStation
  };
}

function getStationDetailsFromOnlyScheduledIntermediateApi({ scheduledIntermediateApiResponse, trainStartDate }) {
  const {
    collectHaltingStationDetailsFromIntermediateSchedule,
    collectIntermediateStationDetailsFromIntermediateSchedule
  } = collectFromIntermediateScheduleForTrainStartDate(trainStartDate);

  const {
    DaysOfRun,
    TrainDetails: { Number: trainNumber, Name: trainName }
  } = scheduledIntermediateApiResponse;

  const haltingStationCollection = scheduledIntermediateApiResponse.Stations.map(
    collectHaltingStationDetailsFromIntermediateSchedule
  );
  scheduledIntermediateApiResponse.Stations.forEach((stationDetailIntermediateSchedule, index) => {
    const intermediateStations = stationDetailIntermediateSchedule.IntermediateStations.map(
      collectIntermediateStationDetailsFromIntermediateSchedule
    );
    haltingStationCollection[index]['intermediateStations'] = intermediateStations;
  });
  const {
    currentStation,
    currentHaltingStation,
    nextStation,
    originStation,
    destinationStation,
    trainStartedFromSource,
    trainReachedDestination,
    trainFullyDiverted,
    trainFullyCancelled,
    firstStoppingStation,
    lastStoppingStation
  } = getStationStatusIntermediateScheduleResponse(haltingStationCollection);
  const result = {
    trainStartDate,
    trainName,
    trainNumber,
    haltingStationCollection,
    originStation,
    destinationStation,
    currentStation,
    nextStation,
    currentHaltingStation,
    trainStartedFromSource,
    trainReachedDestination,
    trainFullyDiverted,
    trainFullyCancelled,
    firstStoppingStation,
    lastStoppingStation,
    totalDistance:
      destinationStation.distanceFromOriginStation && destinationStation.distanceFromOriginStation.toString(),
    timerRefreshTimeStamp: new Date(),
    daysOfRun: getDaysOfRun(DaysOfRun)
  };
  return result;
}

function getDifferenceInDate({ actualDateObject, scheduledDateObject }) {
  return actualDateObject - scheduledDateObject;
}

function getScheduledDateFromAvailableData({ trainStartDate, dayCount }) {
  const trainStartDateObject = getDateObjectFromDetails({ dateInString: trainStartDate, timeInString: '00:00:00' });
  const scheduledDate = addDays(trainStartDateObject, dayCount - 1);
  return getFormattedDateStringForLts(scheduledDate);
}

function getArrivalAndDepartureDateFromIntermediateStation({ intermediateScheduleStationDetail, trainStartDate }) {
  let {
    ArrivalDetails: { scheduledArrivalDate, scheduledArrivalTime },
    DepartureDetails: { scheduledDepartureDate, scheduledDepartureTime },
    Day: dayCount
  } = intermediateScheduleStationDetail;

  if (!scheduledDepartureTime) {
    scheduledDepartureTime = scheduledArrivalTime;
  }
  if (!scheduledArrivalTime) {
    scheduledArrivalTime = scheduledDepartureTime;
  }

  scheduledArrivalDate = scheduledArrivalDate || getScheduledDateFromAvailableData({ trainStartDate, dayCount });
  scheduledDepartureDate = scheduledDepartureDate || getScheduledDateFromAvailableData({ trainStartDate, dayCount });
  const scheduledArrivalDateObject = getDateObjectFromDetails({
    dateInString: scheduledArrivalDate,
    timeInString: scheduledArrivalTime
  });
  const scheduledDepartureDateObject = getDateObjectFromDetails({
    dateInString: scheduledDepartureDate,
    timeInString: scheduledDepartureTime
  });

  if (scheduledDepartureDateObject - scheduledArrivalDateObject < 0) {
    scheduledDepartureDate = getFormattedDateStringForLts(addDays(scheduledDepartureDateObject, 1));
  }
  return { scheduledDepartureDate, scheduledDepartureTime, scheduledArrivalDate, scheduledArrivalTime };
}

function getDaysOfRun(daysOfRun) {
  return LtsConstants.DAY_NAMES.map((day) => (daysOfRun[day] ? '1' : '0')).join('');
}
function getStationDetailsFromBothLiveStatusAndScheduledIntermediateApi({
  liveStatusApiResponse,
  scheduledIntermediateApiResponse
}) {
  const {
    TrainDetails: { StartDate: trainStartDate }
  } = liveStatusApiResponse;
  const {
    DaysOfRun,
    TrainDetails: { Number: trainNumber, Name: trainName }
  } = scheduledIntermediateApiResponse;
  function collectHaltingStationFromLiveStatusResponse(stationDetailLiveStatus) {
    const {
      Station: { name: stationName, code: stationCode, expectedPlatformNumber: platformNumber },
      HaltMinutes: stoppingMinutes,
      ArrivalDetails: {
        actualArrivalDate,
        scheduledArrivalDate,
        actualArrivalTime,
        scheduledArrivalTime,
        arrived: hasArrived
      },
      DepartureDetails: {
        actualDepartureDate,
        scheduledDepartureDate,
        actualDepartureTime,
        scheduledDepartureTime,
        departed: hasDeparted
      },
      Distance: distanceFromOriginStation,
      MetaDetails: {
        StoppingStation: stoppingStation,
        DivertedStation: divertedStation,
        CancelledStation: cancelledStation
      } = { StoppingStation: true, DivertedStation: false, CancelledStation: false }
    } = stationDetailLiveStatus;

    const scheduledArrivalDateObject = getDateObjectFromDetails({
      dateInString: scheduledArrivalDate,
      timeInString: scheduledArrivalTime
    });
    const actualArrivalDateObject = getDateObjectFromDetails({
      dateInString: actualArrivalDate,
      timeInString: actualArrivalTime
    });
    const scheduledDepartureDateObject = getDateObjectFromDetails({
      dateInString: scheduledDepartureDate,
      timeInString: scheduledDepartureTime
    });
    const actualDepartureDateObject = getDateObjectFromDetails({
      dateInString: actualDepartureDate,
      timeInString: actualDepartureTime
    });

    const differenceInScheduledAndActualArrivalTime = getDifferenceInDate({
      actualDateObject: actualArrivalDateObject,
      scheduledDateObject: scheduledArrivalDateObject
    });
    const differenceInScheduledAndActualDepartureTime = getDifferenceInDate({
      actualDateObject: actualDepartureDateObject,
      scheduledDateObject: scheduledDepartureDateObject
    });
    const arrivedStationOnTime = differenceInScheduledAndActualArrivalTime === 0;
    const departedStationOnTime = differenceInScheduledAndActualDepartureTime === 0;

    return {
      stationName,
      stationCode,
      actualDepartureDetails: {
        actualDepartureTime,
        actualDepartureDate
      },
      scheduledDepartureDetails: {
        scheduledDepartureTime,
        scheduledDepartureDate
      },
      actualArrivalDetails: {
        actualArrivalTime,
        actualArrivalDate
      },
      scheduledArrivalDetails: {
        scheduledArrivalTime,
        scheduledArrivalDate
      },
      platformNumber,
      stoppingMinutes: stoppingMinutes.toString(),
      distanceFromOriginStation,
      arrivedStationOnTime,
      departedStationOnTime,
      stoppingStation,
      divertedStation,
      cancelledStation,
      hasArrived,
      hasDeparted
    };
  }

  const haltingStationCollection = liveStatusApiResponse.Stations.map(collectHaltingStationFromLiveStatusResponse);
  const { collectIntermediateStationDetailsFromIntermediateSchedule } =
    collectFromIntermediateScheduleForTrainStartDate(trainStartDate);

  scheduledIntermediateApiResponse.Stations.forEach((stationDetailIntermediateSchedule) => {
    const correspondingHaltingStation = haltingStationCollection.find(
      (haltingStation) => haltingStation.stationCode === stationDetailIntermediateSchedule.Station.code
    );
    if (!correspondingHaltingStation) return;

    const intermediateStations = stationDetailIntermediateSchedule.IntermediateStations.map(
      collectIntermediateStationDetailsFromIntermediateSchedule
    );
    intermediateStations.forEach((intermediateStation) =>
      fillMissingDataForIntermediateStationFromHaltingStation({
        haltingStation: correspondingHaltingStation,
        trainStartDate,
        intermediateStation
      })
    );
    correspondingHaltingStation['intermediateStations'] = intermediateStations;
  });

  const {
    currentStation,
    nextStation,
    trainStartedFromSource,
    trainReachedDestination,
    originStation,
    destinationStation,
    firstStoppingStation,
    lastStoppingStation,
    trainFullyCancelled,
    trainFullyDiverted
  } = getStationStatusBothApiResponse({ liveStatusApiResponse, haltingStationCollection });
  const result = {
    daysOfRun: getDaysOfRun(DaysOfRun),
    trainStartDate,
    trainName,
    trainNumber,
    haltingStationCollection,
    originStation,
    destinationStation,
    currentStation,
    nextStation,
    trainStartedFromSource,
    trainReachedDestination,
    firstStoppingStation,
    lastStoppingStation,
    trainFullyCancelled,
    trainFullyDiverted,
    totalDistance:
      destinationStation.distanceFromOriginStation && destinationStation.distanceFromOriginStation.toString(),
    timerRefreshTimeStamp: new Date()
  };

  return result;
}
